/**
 * Dynamic chunk size calculator optimized for large video files
 * This function is shared across all upload components to ensure consistency
 * 
 * @param {number} totalFileSize - Total file size in bytes
 * @returns {number} Optimized chunk size in bytes
 */
export const getOptimizedChunkSize = (totalFileSize) => {
  const fileSizeInMB = totalFileSize / (1024 * 1024);
  const fileSizeInGB = fileSizeInMB / 1024;
  
  // For very small files, use smaller chunks to avoid overhead
  if (fileSizeInMB < 10) {
    return 1 * 1024 * 1024; // 1MB
  }
  // For small files (10-100MB), use moderate chunks
  else if (fileSizeInMB < 100) {
    return 5 * 1024 * 1024; // 5MB
  }
  // For medium files (100-500MB), use larger chunks
  else if (fileSizeInMB < 500) {
    return 10 * 1024 * 1024; // 10MB
  }
  // For large files (500MB-1GB), use bigger chunks
  else if (fileSizeInMB < 1000) {
    return 25 * 1024 * 1024; // 25MB
  }
  // For very large files (1-5GB), use even bigger chunks
  else if (fileSizeInGB < 5) {
    return 100 * 1024 * 1024; // 100MB
  }
  // For huge files (5-10GB), use large chunks
  else if (fileSizeInGB < 10) {
    return 200 * 1024 * 1024; // 200MB
  }
  // For massive files (10-20GB), use very large chunks
  else if (fileSizeInGB < 20) {
    return 300 * 1024 * 1024; // 300MB
  }
  // For gigantic files (20GB+), use maximum chunks
  else {
    return 500 * 1024 * 1024; // 500MB
  }
};

/**
 * Get formatted file size display string
 * @param {number} sizeInBytes - Size in bytes
 * @returns {string} Formatted size string (e.g., "1.5GB" or "250.3MB")
 */
export const getFormattedFileSize = (sizeInBytes) => {
  const sizeMB = sizeInBytes / (1024 * 1024);
  const sizeGB = sizeMB / 1024;
  
  return sizeGB >= 1 ? `${sizeGB.toFixed(2)}GB` : `${sizeMB.toFixed(2)}MB`;
};

/**
 * Get optimization info for logging purposes
 * @param {number} fileSize - File size in bytes
 * @param {number} chunkSize - Chunk size in bytes
 * @returns {object} Optimization information object
 */
export const getOptimizationInfo = (fileSize, chunkSize) => {
  const totalChunks = Math.ceil(fileSize / chunkSize);
  const chunkSizeMB = chunkSize / (1024 * 1024);
  const displaySize = getFormattedFileSize(fileSize);
  
  return {
    displaySize,
    chunkSizeMB: chunkSizeMB.toFixed(0),
    totalChunks,
    logMessage: `📊 [UPLOAD OPTIMIZATION] File: ${displaySize} | Using ${chunkSizeMB.toFixed(0)}MB chunks | Total chunks: ${totalChunks}`
  };
};